import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useEnsurePermissionsToken } from '@grc/ui-utility';

// How long the portal waits, once the user is signed in, for useEnsurePermissionsToken to fetch
// a token that carries the user's permissions. Auth0 assigns the user's role during the first
// login, so that login's token has none; the hook refreshes it (within 9 s) and the portal holds
// the layout, which calls APIs as soon as it mounts, until the refresh is done. A refresh that
// hangs in the SDK's 60 s iframe timeout must not hold the portal that long, so past this limit
// the layout renders with the token the user already has. Same limit as the TMS shell.
export const PERMISSIONS_TOKEN_LIMIT_MS = 10_000;

// True once the signed-in user's token carries permissions, or the wait above has run out.
const usePermissionsTokenReady = (): boolean => {
  const { user } = useAuth0();
  const permissionsToken = useEnsurePermissionsToken();
  const [waitTimedOut, setWaitTimedOut] = useState(false);

  const awaitingPermissionsToken = !!user && permissionsToken === 'checking';

  useEffect(() => {
    if (!awaitingPermissionsToken) {
      return undefined;
    }
    const timer = setTimeout(
      () => setWaitTimedOut(true),
      PERMISSIONS_TOKEN_LIMIT_MS
    );
    return () => clearTimeout(timer);
  }, [awaitingPermissionsToken]);

  return permissionsToken === 'ready' || waitTimedOut;
};

export default usePermissionsTokenReady;
